// Gentle generative instrumentals (piano, saxophone, guitar) synthesized live — no audio files.
const midi = (n) => 440 * Math.pow(2, (n - 69) / 12);

const PIECES = [
  {
    name: "Quiet Piano", bpm: 66,
    chords: [[48, 55, 64, 71], [45, 52, 60, 67], [41, 48, 57, 64], [43, 50, 59, 65]],
    melody: [76, null, 74, 72, null, 71, 72, null, 69, null, 72, 74, 72, null, 67, null],
    lead: "piano", comp: "piano",
  },
  {
    name: "Evening Saxophone", bpm: 60,
    chords: [[50, 57, 65, 72], [43, 50, 59, 65], [48, 55, 64, 71], [45, 52, 60, 67]],
    melody: [69, null, null, 72, 74, null, 72, null, 71, null, null, 67, 69, null, null, null],
    lead: "sax", comp: "piano",
  },
  {
    name: "Acoustic Calm", bpm: 72,
    chords: [[43, 50, 55, 59, 62], [40, 47, 52, 55, 59], [36, 43, 48, 52, 55], [38, 45, 50, 54, 57]],
    melody: [null, 71, null, 74, null, 72, null, 71, null, 69, null, 67, null, 69, null, null],
    lead: "guitar", comp: "guitar",
  },
];

export class MusicEngine {
  constructor() { this.ctx = null; this.timer = null; this.index = 0; this.playing = false; }

  get pieceName() { return PIECES[this.index].name; }

  _ensure() {
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0;
    const verb = this.ctx.createConvolver();
    const len = this.ctx.sampleRate * 2.4, ir = this.ctx.createBuffer(2, len, this.ctx.sampleRate);
    for (let c = 0; c < 2; c++) { const d = ir.getChannelData(c); for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3); }
    verb.buffer = ir;
    const wet = this.ctx.createGain(); wet.gain.value = 0.35;
    this.bus = this.ctx.createGain();
    this.bus.connect(this.master); this.bus.connect(verb); verb.connect(wet); wet.connect(this.master);
    this.master.connect(this.ctx.destination);
  }

  _piano(f, t, dur, vel = 0.12) {
    const c = this.ctx, g = c.createGain(), lp = c.createBiquadFilter();
    lp.type = "lowpass"; lp.frequency.value = 2400;
    [1, 2, 3].forEach((h, i) => {
      const o = c.createOscillator(); o.type = i ? "sine" : "triangle"; o.frequency.value = f * h;
      const hg = c.createGain(); hg.gain.value = [1, 0.25, 0.08][i];
      o.connect(hg); hg.connect(lp); o.start(t); o.stop(t + dur + 1.5);
    });
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(vel, t + 0.01); g.gain.exponentialRampToValueAtTime(0.0008, t + dur + 1.4);
    lp.connect(g); g.connect(this.bus);
  }

  _sax(f, t, dur) {
    const c = this.ctx, o = c.createOscillator(), lp = c.createBiquadFilter(), bp = c.createBiquadFilter(), g = c.createGain();
    o.type = "sawtooth"; o.frequency.value = f;
    const vib = c.createOscillator(), vg = c.createGain(); vib.frequency.value = 5; vg.gain.setValueAtTime(0, t); vg.gain.linearRampToValueAtTime(f * 0.012, t + 0.5);
    vib.connect(vg); vg.connect(o.frequency);
    lp.type = "lowpass"; lp.frequency.setValueAtTime(700, t); lp.frequency.linearRampToValueAtTime(1600, t + 0.25); lp.Q.value = 1.2;
    bp.type = "peaking"; bp.frequency.value = 1200; bp.gain.value = 5;
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.07, t + 0.12); g.gain.setValueAtTime(0.07, t + dur * 0.8); g.gain.linearRampToValueAtTime(0, t + dur + 0.3);
    o.connect(lp); lp.connect(bp); bp.connect(g); g.connect(this.bus);
    o.start(t); vib.start(t); o.stop(t + dur + 0.4); vib.stop(t + dur + 0.4);
  }

  _guitar(f, t, vel = 0.1) {
    const c = this.ctx, o = c.createOscillator(), o2 = c.createOscillator(), lp = c.createBiquadFilter(), g = c.createGain();
    o.type = "sawtooth"; o2.type = "triangle"; o.frequency.value = f; o2.frequency.value = f * 1.003;
    lp.type = "lowpass"; lp.frequency.setValueAtTime(3200, t); lp.frequency.exponentialRampToValueAtTime(500, t + 0.6);
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(vel, t + 0.004); g.gain.exponentialRampToValueAtTime(0.0008, t + 2.2);
    o.connect(lp); o2.connect(lp); lp.connect(g); g.connect(this.bus);
    o.start(t); o2.start(t); o.stop(t + 2.3); o2.stop(t + 2.3);
  }

  _scheduleBar(at) {
    const p = PIECES[this.index], beat = 60 / p.bpm, chord = p.chords[this.bar % p.chords.length];
    if (p.comp === "guitar") {
      const order = [0, 2, 3, 4, 1, 3, 2, 4];
      order.forEach((n, i) => this._guitar(midi(chord[n % chord.length]), at + i * beat / 2, 0.07));
    } else {
      chord.forEach((n, i) => this._piano(midi(n), at + i * 0.03, beat * 3.5, p.lead === "sax" ? 0.05 : 0.06));
    }
    for (let i = 0; i < 4; i++) {
      const n = p.melody[(this.bar % 4) * 4 + i];
      if (n == null) continue;
      const t = at + i * beat;
      if (p.lead === "sax") this._sax(midi(n - 12), t, beat * 1.8);
      else if (p.lead === "guitar") this._guitar(midi(n), t, 0.1);
      else this._piano(midi(n), t, beat * 1.5, 0.09);
    }
    this.bar += 1;
    return at + beat * 4;
  }

  _loop() {
    while (this.nextAt < this.ctx.currentTime + 1.2) this.nextAt = this._scheduleBar(this.nextAt);
  }

  async play() {
    this._ensure();
    if (this.ctx.state === "suspended") await this.ctx.resume();
    this.playing = true;
    this.bar = 0;
    this.nextAt = this.ctx.currentTime + 0.1;
    this.master.gain.cancelScheduledValues(this.ctx.currentTime);
    this.master.gain.linearRampToValueAtTime(0.7, this.ctx.currentTime + 1.5);
    clearInterval(this.timer);
    this.timer = setInterval(() => this._loop(), 250);
    this._loop();
  }

  stop() {
    if (!this.ctx) return;
    this.playing = false;
    clearInterval(this.timer);
    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setValueAtTime(this.master.gain.value, now);
    this.master.gain.linearRampToValueAtTime(0, now + 0.6);
  }

  next() {
    this.index = (this.index + 1) % PIECES.length;
    if (this.playing) { this.bar = 0; this.nextAt = this.ctx.currentTime + 0.4; }
    return this.pieceName;
  }
}
